import 'reflect-metadata';
import { ItemAddonBehaviour, ItemAddonType } from '@order-mage/integration-sdk';
import { MAX_ADDON_NESTING_DEPTH } from '@/api/menu-list/max-addon-nesting-depth';
import type {
    StorefrontAddonDef,
    StorefrontAddonMemberRef,
    StorefrontItemBody,
} from '@/api/menu-list/storefront-menu-snapshot.types';
import { getMemberHash, getHashFromItemIdMap, isItemPriceConfigured } from '@/entities/cart';
import type { ResolvedStorefrontItem } from '@/entities/product/lib/storefront-menu';

export type AddonValue = string | Record<string, number>;
export type AddonSelections = Record<string, AddonValue>;

export type ItemsMap = Record<string, ResolvedStorefrontItem>;

const isContained = (member: StorefrontAddonMemberRef): boolean => Number(member.amount?.value ?? 0) > 0;

const isSelectable = (
    member: StorefrontAddonMemberRef,
    itemsMap: ItemsMap,
    idToHash: ReturnType<typeof getHashFromItemIdMap>,
): boolean => {
    const hash = getMemberHash(member, idToHash);
    const body = hash ? itemsMap[hash]?.body : undefined;
    return !!body && isItemPriceConfigured(body);
};

const computeDefinitionEntry = (
    addonDef: Pick<StorefrontAddonDef, 'id' | 'behaviour' | 'minAmount' | 'maxAmount'>,
    members: StorefrontAddonMemberRef[],
    itemsMap: ItemsMap,
    idToHash: ReturnType<typeof getHashFromItemIdMap>,
): [string, AddonValue][] => {
    if (!addonDef.id || !members.length) return [];

    if (addonDef.behaviour === ItemAddonBehaviour.SINGLE_PICK) {
        const contained = members.find(
            (member) => isContained(member) && isSelectable(member, itemsMap, idToHash),
        );
        return [[addonDef.id, (contained && getMemberHash(contained, idToHash)) || '']];
    }

    return [
        [
            addonDef.id,
            Object.fromEntries(
                members
                    .filter((member) => isContained(member) && isSelectable(member, itemsMap, idToHash))
                    .map((member): [string, number] => [
                        getMemberHash(member, idToHash) || '',
                        Number(member.amount?.value ?? 0),
                    ])
                    .filter(([hash]) => hash),
            ),
        ],
    ];
};

const fromStorefrontAddon = (
    addon: StorefrontAddonDef,
    itemsMap: ItemsMap,
    idToHash: ReturnType<typeof getHashFromItemIdMap>,
): [string, AddonValue][] => {
    if (addon.type === ItemAddonType.GROUP && addon.groupMembers?.length) {
        return addon.groupMembers.flatMap((nested) => fromStorefrontAddon(nested, itemsMap, idToHash));
    }

    return computeDefinitionEntry(addon, addon.members, itemsMap, idToHash);
};

const sectionDefs = (addon: StorefrontAddonDef): StorefrontAddonDef[] =>
    addon.type === ItemAddonType.GROUP && addon.groupMembers?.length ? addon.groupMembers : [addon];

const walkAddons = (
    addons: StorefrontAddonDef[] | undefined,
    itemsMap: ItemsMap,
    idToHash: ReturnType<typeof getHashFromItemIdMap>,
    depth: number,
    seen: Set<string>,
): [string, AddonValue][] => {
    if (!addons?.length || depth >= MAX_ADDON_NESTING_DEPTH) return [];
    const entries: [string, AddonValue][] = [];
    for (const addon of addons) {
        entries.push(...fromStorefrontAddon(addon, itemsMap, idToHash));
        for (const def of sectionDefs(addon)) {
            for (const member of def.members) {
                const hash = getMemberHash(member, idToHash);
                const child = hash ? itemsMap[hash]?.body : undefined;
                if (!child?.id || seen.has(child.id)) continue;
                const nextSeen = new Set(seen);
                nextSeen.add(child.id);
                entries.push(...walkAddons(child.addons, itemsMap, idToHash, depth + 1, nextSeen));
                for (const row of child.variants ?? []) {
                    if (row.isDefault) continue;
                    const variantHash = idToHash.get(row.itemId);
                    const variantBody = variantHash ? itemsMap[variantHash]?.body : undefined;
                    if (!variantBody?.id || nextSeen.has(variantBody.id)) continue;
                    const variantSeen = new Set(nextSeen);
                    variantSeen.add(variantBody.id);
                    entries.push(
                        ...walkAddons(variantBody.addons, itemsMap, idToHash, depth + 1, variantSeen),
                    );
                }
            }
        }
    }
    return entries;
};

export const computeAddonDefaults = (
    item: Pick<StorefrontItemBody, 'addons'> | undefined,
    itemsMap: ItemsMap,
): AddonSelections => {
    const idToHash = getHashFromItemIdMap(itemsMap);
    return Object.fromEntries(walkAddons(item?.addons, itemsMap, idToHash, 0, new Set()));
};
